import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "./config";

const MyTimetable = () => {
  const navigate = useNavigate();
  const [teacherEmail, setTeacherEmail] = useState("");
  const [teacherTimetable, setTeacherTimetable] = useState(null);
  const [loadingTimetable, setLoadingTimetable] = useState(false);
  const [timetableError, setTimetableError] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);

  const server = buildApiUrl("");

  // Print to PDF function
  const handlePrintTimetable = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setIsPrinting(false), 100);
    }, 100);
  };

  // Load admin template periods on mount
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        setLoadingPeriods(true);
        const response = await fetch(`${server}/admin-global-template`);
        const data = await response.json();
        
        if (data.periods && data.periods.length > 0) {
          setPeriods(data.periods);
        } else {
          console.warn('⚠️ No periods found in admin template');
        }
      } catch (error) {
        console.error('❌ Error loading periods:', error);
      } finally {
        setLoadingPeriods(false);
      }
    };
    
    loadPeriods();
  }, [server]);

  // Get teacher email from localStorage
  useEffect(() => {
    const storedTeacherEmail = localStorage.getItem('teacherEmail');
    const userRole = localStorage.getItem('userRole');
    
    if (!storedTeacherEmail || userRole !== 'teacher') {
      navigate('/teacher');
      return;
    }
    
    setTeacherEmail(storedTeacherEmail);
  }, [navigate]);

  // Load teacher timetable
  const loadTeacherTimetable = async () => {
    if (!teacherEmail) {
      return;
    }

    setLoadingTimetable(true);
    setTimetableError(null);

    try {
      const response = await fetch(`${server}/teacher-timetable/${encodeURIComponent(teacherEmail)}`);
      const data = await response.json();
      
      // Debug: Log the actual data received
      console.log('🔍 Teacher Timetable Data:', {
        success: data.success,
        source: data.source,
        totalAssignments: data.total_assignments,
        hasAssignments: data.has_assignments,
        classes: data.timetable?.days ? 
          [...new Set(Object.values(data.timetable.days).flat().map(p => p.class))].filter(Boolean) : []
      });

      if (data.success && data.timetable) {
        setTeacherTimetable(data.timetable);
      } else {
        setTimetableError(data.message || "No timetable found for this teacher");
      }
    } catch (error) {
      console.error('❌ Error loading teacher timetable:', error);
      setTimetableError("Failed to load timetable");
    } finally {
      setLoadingTimetable(false);
    }
  };

  // Load timetable when teacher email is available
  useEffect(() => {
    if (teacherEmail && teacherEmail !== "teacher@school.com") {
      loadTeacherTimetable();
    }
  }, [teacherEmail]);

  // Time formatting helper
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      // Handle both HH:MM and HH:MM:SS formats
      const timeParts = timeStr.split(':');
      const hours = parseInt(timeParts[0]);
      const minutes = timeParts[1];
      
      if (hours === 0) return `12:${minutes} AM`;
      if (hours < 12) return `${hours}:${minutes} AM`;
      if (hours === 12) return `12:${minutes} PM`;
      return `${hours - 12}:${minutes} PM`;
    } catch (error) {
      return timeStr;
    }
  };

  // Get complete schedule for a specific day (including free periods)
  const getDaySchedule = (day) => {
    // Get teacher's assignments for this day
    const teacherAssignments = teacherTimetable?.days?.[day] || [];
    
    // Create a map of period ID to assignment
    const assignmentMap = {};
    teacherAssignments.forEach(assignment => {
      const periodData = assignment.period;
      const periodId = assignment.periodId || periodData?.id;
      
      if (periodId) {
        assignmentMap[periodId] = assignment;
        
        // For merged periods, also map all original period IDs to this assignment
        if (periodData?.isMerged && periodData?.originalPeriods) {
          const mapOriginalPeriods = (periods) => {
            periods.forEach(originalPeriod => {
              if (originalPeriod.id) {
                assignmentMap[originalPeriod.id] = assignment;
              }
              // Handle nested merged periods
              if (originalPeriod.isMerged && originalPeriod.originalPeriods) {
                mapOriginalPeriods(originalPeriod.originalPeriods);
              }
            });
          };
          mapOriginalPeriods(periodData.originalPeriods);
        }
      }
    });
    
    // Generate complete schedule with all periods
    return periods.map(period => {
      // Convert period.id to string to match the keys in assignmentMap
      const periodIdString = String(period.id);
      const assignment = assignmentMap[periodIdString];
      
      if (assignment) {
        // Teacher has an assignment for this period
        // Use the template period (not the merged period from assignment)
        // so that period.id matches the template period.id during rendering
        return {
          ...assignment,
          period: period,  // Use template period, not assignment.period
          isFree: false
        };
      } else {
        // Free period
        return {
          id: `free-${day}-${period.id}`,
          period: period,
          subject: "Free Period",
          class: "",
          form: "",
          room: "",
          students: [],
          isFree: true
        };
      }
    });
  };

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

  // Calculate which cells should be merged (for visual display)
  const calculateMergedCells = (day) => {
    const daySchedule = getDaySchedule(day);
    const mergedInfo = {};
    
    // Track the current group being built
    let currentGroupStart = null;
    let currentGroupCount = 0;
    let currentSubject = null;
    let currentClass = null;
    let currentRoom = null;
    
    periods.forEach((period, index) => {
      const assignment = daySchedule.find(p => p.period?.id === period.id);
      const isSameAsGroup = assignment && 
                           !assignment.isFree &&
                           assignment.subject === currentSubject &&
                           assignment.class === currentClass &&
                           assignment.room === currentRoom;
      
      if (isSameAsGroup) {
        // Continue the current group
        currentGroupCount++;
        mergedInfo[period.id] = { skip: true, rowSpan: 0 };
        // Update the first cell's rowSpan
        if (currentGroupStart) {
          mergedInfo[currentGroupStart].rowSpan = currentGroupCount;
        }
      } else {
        // Start a new group
        if (assignment && !assignment.isFree) {
          currentGroupStart = period.id;
          currentGroupCount = 1;
          currentSubject = assignment.subject;
          currentClass = assignment.class;
          currentRoom = assignment.room;
          mergedInfo[period.id] = { skip: false, rowSpan: 1 };
        } else {
          // Free period - reset group
          currentGroupStart = null;
          currentGroupCount = 0;
          currentSubject = null;
          currentClass = null;
          currentRoom = null;
          mergedInfo[period.id] = { skip: false, rowSpan: 1 };
        }
      }
    });
    
    return mergedInfo;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-container {
            width: 100%;
            max-width: 100%;
          }
          
          table {
            page-break-inside: avoid;
          }
          
          .print-title {
            text-align: center;
            margin-bottom: 10px;
            font-size: 18px;
            font-weight: bold;
          }
          
          .print-info {
            text-align: center;
            margin-bottom: 15px;
            font-size: 12px;
          }
        }
      `}</style>

      {/* Header */}
      <div className="bg-white shadow-sm border-b no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/teacher')}
                className="text-gray-600 hover:text-indigo-600 transition-colors"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">📅 My Timetable</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePrintTimetable}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
              >
                <span>🖨️</span>
                <span>Print to PDF</span>
              </button>
              <div className="text-sm text-gray-600">
                {teacherEmail}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print-container">
        {/* Print Header - Only visible when printing */}
        {isPrinting && teacherTimetable && (
          <div className="hidden print:block mb-4">
            <div className="print-title">Teacher Timetable</div>
            <div className="print-info">
              <div>{teacherEmail}</div>
              <div>Generated: {new Date().toLocaleDateString()}</div>
            </div>
          </div>
        )}

        {(loadingTimetable || loadingPeriods) && (
          <div className="text-center py-12 no-print">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your timetable...</p>
          </div>
        )}

        {timetableError && !loadingTimetable && !loadingPeriods && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center no-print">
            <div className="text-red-600 text-xl mb-2">❌</div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">No Timetable Found</h3>
            <p className="text-red-600 mb-4">{timetableError}</p>
            <button
              onClick={loadTeacherTimetable}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              🔄 Try Again
            </button>
          </div>
        )}

        {teacherTimetable && !loadingTimetable && !loadingPeriods && periods.length > 0 && (
          <div className="space-y-4">
            {/* Timetable Header */}
            {teacherTimetable && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 no-print">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-indigo-800">{teacherTimetable.name}</h2>
                    <p className="text-sm text-indigo-600">{teacherTimetable.description}</p>
                  </div>
                  <div className="text-right text-xs text-indigo-600">
                    <p>Generated: {new Date(teacherTimetable.generatedAt).toLocaleDateString()}</p>
                    <p>Total Classes: {Object.values(teacherTimetable.days || {}).flat().length}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Weekly Timetable Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-900 border-r border-gray-200">
                        Time
                      </th>
                      {days.map((day) => (
                        <th key={day} className="px-2 py-2 text-center text-xs font-medium text-gray-900 border-r border-gray-200 last:border-r-0">
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {periods.map((period, periodIndex) => {
                      const hasAssignments = teacherTimetable && Object.values(teacherTimetable.days || {}).flat().length > 0;
                      return (
                        <tr key={period.id} className="hover:bg-gray-50">
                          {/* Period Time Column */}
                          <td className="px-2 py-1 border-r border-gray-200 bg-gray-50">
                            <div className="text-xs">
                              <div className="font-medium text-gray-900">
                                {period.name}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                {formatTime(period.startTime)} - {formatTime(period.endTime)}
                              </div>
                            </div>
                          </td>
                          {/* Day Columns */}
                          {days.map((day) => {
                            const daySchedule = getDaySchedule(day);
                            let cellInfo = { skip: false, rowSpan: 1 };
                            if (hasAssignments) {
                              const mergedCells = calculateMergedCells(day);
                              cellInfo = mergedCells[period.id] || { skip: false, rowSpan: 1 };
                            }
                            if (cellInfo.skip) {
                              return null;
                            }
                            const periodAssignment = daySchedule.find(p => p.period?.id === period.id);
                            const rowSpan = cellInfo.rowSpan || 1;
                            return (
                              <td 
                                key={`${day}-${period.id}`} 
                                className="px-2 py-1 border-r border-gray-200 last:border-r-0"
                                rowSpan={rowSpan}
                              >
                                {periodAssignment ? (
                                  <div className={`rounded p-2 text-center min-h-[50px] flex flex-col justify-center ${
                                    periodAssignment.isFree 
                                      ? 'bg-gray-100 text-gray-500' 
                                      : 'bg-blue-100 text-blue-800 border border-blue-200'
                                  }`}>
                                    <div className="font-semibold text-xs mb-0.5">
                                      {periodAssignment.isFree ? 'Free' : periodAssignment.subject}
                                    </div>
                                    {!periodAssignment.isFree && (
                                      <>
                                        {periodAssignment.class && (
                                          <div className="text-[10px] mb-0.5">
                                            {periodAssignment.class} {periodAssignment.form && `(${periodAssignment.form})`}
                                          </div>
                                        )}
                                        {periodAssignment.room && (
                                          <div className="text-[10px]">
                                            {periodAssignment.room}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="rounded p-2 text-center min-h-[50px] flex flex-col justify-center bg-gray-100 text-gray-500">
                                    <div className="font-semibold text-xs">Free</div>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Legend */}
              <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-4 text-xs">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded"></div>
                    <span className="text-gray-600">Teaching</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-gray-100 rounded"></div>
                    <span className="text-gray-600">Free</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-yellow-100 border border-yellow-200 rounded"></div>
                    <span className="text-gray-600">Break</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Refresh Button */}
            <div className="text-center pt-4 no-print">
              <button
                onClick={loadTeacherTimetable}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                🔄 Refresh Timetable
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTimetable;